<script setup lang="ts">
import PartFooterNavigation from '@parts/part-footer-navigation.vue';
import PartTitleHead from '@parts/part-title-head.vue';
import PartKeyBox from '@parts/part-key-box.vue';
import SectionSettings from '@sections/section-settings.vue';
import ButtonPrimary from '@buttons/button-primary.vue';
import CoreIcon from '@core/core-icon.vue';

import {useI18n} from 'vue-i18n';
import {useSettings} from '@composables/settings';
import {computed, onBeforeMount, ref} from 'vue';
import {useKeyApi} from "@store/api/keyApi";
import CoreLoader from "@core/core-loader.vue";
import {useToast} from "vue-toast-notification";
import isInTelegram from "@/utils/isInTelegram";
import {hapticFeedback, openLink} from "@tma.js/sdk-vue";
import {useRouter} from 'vue-router';

const {t} = useI18n();
const {
	currentGroup,
	checkCurrentPageAccess,
} = useSettings();
const keyApi = useKeyApi();
const toast = useToast();
const router = useRouter();

const isLoading = ref<boolean>(false);
const loaderText = ref<string>(t('common.loader.key'));
const subLink = ref<string>('');

const fields = computed(() => {
	return {
		title: t('settingsKey.title'),
		subtitle: t('settingsKey.subtitle'),
		info: t('settingsKey.info'),
		downloadCard: {
			title: t('settingsKey.downloadCard.title'),
			subtitle: t('settingsKey.downloadCard.subtitle'),
			button: t('settingsKey.downloadCard.button'),
			hint: t('settingsKey.downloadCard.hint'),
		},
		goToHome: t('settingsKey.goToHome'),
	}
});

const afterCopyKeyHandler = async () => {
	try {
		toast.info(t('common.toast.copied_to_clipboard'), {
			position: 'top'
		});
	} catch (e) {
		console.log(e);
	}
}

const openSubLink = () => {
	if (!subLink.value) return;
	if (isInTelegram() && hapticFeedback.impactOccurred.isAvailable()) {
		hapticFeedback.impactOccurred('medium');
	}
	if (isInTelegram() && openLink.isAvailable()) {
		openLink(subLink.value, {tryInstantView: false});
	} else {
		window.open(subLink.value, '_blank');
	}
};

const goToHome = () => {
	router.push({name: 'Home'});
};

onBeforeMount(async () => {
	const stateSubLink = window.history.state?.subLink as string;
	if (stateSubLink) {
		subLink.value = stateSubLink;
	} else {
		loaderText.value = t('common.loader.key');
		isLoading.value = true;
		const keysInfo = await keyApi.getList();
		subLink.value = keysInfo.sub_link_new || keysInfo.sub_link || '';
		isLoading.value = false;
	}
});

checkCurrentPageAccess();
</script>

<template>
	<main class="page page-key">
		<section-settings
			v-if="currentGroup"
		>
			<!-- 1) Убран section-label "КЛЮЧ ДОСТУПА" -->
			<!-- 2) Сокращён отступ между текстом и ключом -->
			<transition
				appear
				name="fade"
				style="--animation-delay: 0.1s"
			>
				<part-title-head
					:title="fields.title"
					:subtitle="fields.subtitle"
					title-font="font-18-pro-medium"
					class="page-key__content"
				/>
			</transition>

			<transition
				v-if="!isLoading"
				appear
				name="show-up"
				style="--animation-delay: 0.3s"
			>
				<div>
					<part-key-box
						:value="subLink"
						:after-copy="afterCopyKeyHandler"
						class="page-key__key"
					/>

					<!-- 4) Иконка info в кружочке -->
					<div class="page-key__info font-14-pro-regular">
						<span class="page-key__info-icon">i</span>
						{{ fields.info }}
					</div>
				</div>
			</transition>
			<transition
				v-else
				appear
				name="show-up"
				style="--animation-delay: 0.3s"
			>
				<core-loader
					:title="loaderText"
				/>
			</transition>

			<!-- 5) Форматирование карточки "Ещё нет приложения?" -->
			<transition
				v-if="!isLoading"
				appear
				name="show-up"
				style="--animation-delay: 0.5s"
			>
				<div class="page-key__download-card">
					<div class="page-key__download-card__title font-18-pro-medium">
						{{ fields.downloadCard.title }}
					</div>
					<div class="page-key__download-card__subtitle font-14-pro-regular">
						{{ fields.downloadCard.subtitle }}
					</div>
					<button-primary
						@click.native="openSubLink"
						:icon="{name: 'download', viewBox: '0 0 24 24'}"
						class="page-key__download-card__button"
					>
						{{ fields.downloadCard.button }}
					</button-primary>
					<!-- 6) Иконка открытого замочка вместо эмодзи -->
					<div class="page-key__download-card__hint font-12-pro-regular">
						<core-icon name="lock-open" viewBox="0 0 16 16" class="page-key__download-card__hint-icon" />
						{{ fields.downloadCard.hint }}
					</div>
				</div>
			</transition>

			<!-- 3) Кнопка "На главную" в стиле Primary -->
			<part-footer-navigation
				v-if="!isLoading"
				class="page-key__footer"
			>
				<button-primary
          pink
					@click.native="goToHome"

				>
					{{ fields.goToHome }}
				</button-primary>
			</part-footer-navigation>

		</section-settings>
	</main>
</template>

<style lang="scss">
.page-key {
	display: flex;
	flex-direction: column;
	flex: 1 1 auto;

	&__content {
		--title-gap: 1.2rem;
		margin-bottom: 1.6rem;
		@include min-desktop {
			margin-bottom: 2.4rem;
		}
	}

	&__key {
		margin-bottom: 1.2rem;
	}

	&__info {
		display: flex;
		align-items: flex-start;
		gap: 0.6rem;
		opacity: 0.6;
		margin-bottom: 2.4rem;

		&-icon {
			flex-shrink: 0;
			width: 1.6rem;
			height: 1.6rem;
			border-radius: 50%;
			background-color: rgba(var(--color-text-rgb), 0.35);
			color: #fff;
			font-size: 0.9rem;
			font-weight: 600;
			font-style: normal;
			line-height: 1.6rem;
			text-align: center;
			margin-top: 0.15rem;
		}
	}

	&__download-card {
		padding: 2rem 2.4rem;
		border-radius: 2.4rem;
		background-color: var(--color-panel);
		border: 1px solid var(--color-panel);
		margin-bottom: 2.4rem;

		&__title {
			margin-bottom: 0.8rem;
		}

		&__subtitle {
			opacity: 0.6;
			margin-bottom: 2rem;
		}

		&__button {
			width: 100%;
			margin-bottom: 1.2rem;
			--button-icon-size: 1.8rem;
		}

		&__hint {
			display: flex;
			align-items: center;
			justify-content: center;
			gap: 0.4rem;
			opacity: 0.5;

			&-icon {
				width: 1.4rem;
				height: 1.4rem;
				fill: none;
				stroke: currentColor;
			}
		}
	}

	&__home-button.button-primary.button-primary_pink {
		background: var(--color-panel);
		color: var(--color-active);
		border-color: var(--color-panel);

		@include theme-dark {
			background-color: var(--color-panel);
			border-color: transparent;
		}
	}

	&__footer {
		margin-top: auto;
		@include min-desktop {
			margin-top: 4rem;
		}
	}
}
</style>
